import { excludeNullOrUndefined } from 'checkout/utils'
import { DMY_CASUAL_FORMAT } from 'constants/dateFormats'
import { isInstantBookingHotelItem, isInsuranceItem, isInsuranceProtectionItem } from 'lib/checkout'
import createSelector from 'lib/createSelector'
import moment from 'moment'
import { sum } from 'lib/arrayUtils'
import { arrayResultMemoizeOptions } from 'checkout/lib/utils/misc/arrayResultMemoizeOptions'
import { getAccommodationItems } from './accommodation'
import { isEnabledForFeature, isFeatureFlagIncluded } from 'lib/featureFlagUtils'
import config from 'constants/config'

export const getInsuranceItems = createSelector(
  (state: App.State) => state.checkout.cart.items,
  (items): Array<App.Checkout.InsuranceItem> => items.filter(isInsuranceProtectionItem),
  arrayResultMemoizeOptions,
)

export const getInsuranceEnabledCheckoutItems = createSelector(
  getAccommodationItems,
  (items): App.Checkout.InstantBookingItem[] => items
    .filter(isInstantBookingHotelItem)
    .filter(item => isEnabledForFeature(config.UNIVERSAL_CHECKOUT_INSURANCE_ENABLED_LIST, item.itemType))
    .filter(item => !isFeatureFlagIncluded(config.INSURANCE_EXCLUDED_OFFER_LIST, item.offerId)),
  arrayResultMemoizeOptions,
)

// It includes the no-protection insurance item
export const getInsuranceTypeItems = createSelector(
  (state: App.State) => state.checkout.cart.items,
  (items): Array<App.Checkout.InsuranceItem> => items.filter(isInsuranceItem),
  arrayResultMemoizeOptions,
)

export const getInsuranceItemsView = createSelector(
  (state: App.State) => getInsuranceItems(state),
  (state: App.State) => state.insurance.quotes,
  (state: App.State) => state.insurance.products,
  (insuranceItems, quotes, products): App.WithDataStatus<Array<App.Checkout.InsuranceItemView>> => {
    let hasRequiredData = true
    const itemViews = insuranceItems.map(item => {
      // quoteId actually changes on every fetch request
      // use productId instead
      const quote = quotes.find(quote => quote.productId === item.productId)
      if (!quote) {
        hasRequiredData = false
        return null
      }

      const product = products.find(product => product.id === item.productId)
      if (!product) {
        hasRequiredData = false
        return null
      }

      const selectedPolicyIds = new Set(item.policyIds)
      const selectedPolicyNames = product.policyOptions?.filter(option => selectedPolicyIds.has(option.policy))
        .map(option => option.name) || []

      const policyNames = product.policyNames?.length ? product.policyNames : selectedPolicyNames

      return {
        itemId: item.itemId,
        quoteId: quote.id,
        productId: item.productId,
        name: product.name,
        description: product.description,
        policyNames,
        startDate: quote.startDate,
        endDate: quote.endDate,
        destinationCountries: quote.destinationCountries,
        price: quote.total,
        value: 0,
        surcharge: 0,
        propertyFees: 0,
        taxesAndFees: 0,
      }
    }).filter(excludeNullOrUndefined)

    return {
      hasRequiredData,
      data: itemViews,
    }
  },
)

function getInsuranceBreakdownItem(item: App.Checkout.InsuranceItemView): App.Checkout.ItemBreakdownView {
  return {
    title: item.policyNames.length ? '' : item.name,
    price: item.price,
    itemType: 'insurance',
    additionalInfoText: [
      ...item.policyNames,
      `${moment(item.startDate).format(DMY_CASUAL_FORMAT)} - ${moment(item.endDate).format(DMY_CASUAL_FORMAT)}`,
      `Covered in: ${item.destinationCountries.join(', ')}`,
    ],
    additionalElements: [],
    taxesAndFees: item.taxesAndFees,
  }
}

export const getInsuranceBreakdownView = createSelector(
  getInsuranceItemsView,
  (viewWithStatus): App.WithDataStatus<Array<App.Checkout.PriceBreakdownView>> => {
    if (viewWithStatus.data.length === 0) { return { hasRequiredData: viewWithStatus.hasRequiredData, data: [] } }

    const breakdownItems = viewWithStatus.data.map(getInsuranceBreakdownItem)
    const breakdownView = {
      title: 'Travel Insurance',
      price: sum(breakdownItems, item => item.price || 0),
      additionalInfoText: [],
      additionalElements: [],
      items: breakdownItems,
    }

    return {
      hasRequiredData: true,
      data: [breakdownView],
    }
  },
)
